
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import { toErrorMessage } from '@/utils/errorHandling';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import Toast from '@/utils/toast';
import '@/validation-rules';

const namespace: string = 'rallyIntelligence';

@Component<PromptResultsModal>({
	components: {
		StandardModal,
		TextButton,
		IconButton,
		ValidationProvider
	}
})
export default class PromptResultsModal extends Vue {
	@Prop({ type: Number, default: 25 }) private numberOfPeople!: number;
	@Prop({ type: Number, default: 1000 }) private totalBudget!: number;
	@Prop({ type: Number, default: null }) private promptId!: number | null;
	@Prop({ type: Array, required: true }) private initialPackages!: CateringPackage[];
	@Prop({ type: Array, default: () => [] }) private dietaryPreferences!: string[];
	@Prop({ type: Array, default: () => [] }) private allergens!: string[];

	@Getter('getMonthlyTokensRemaining', { namespace }) private tokensRemaining!: number;

	@Action('fetchUsage', { namespace }) private fetchUsage!: () => Promise<void>;
	@Action('refinePackage', { namespace: 'cateringBot' }) private refinePackage!: (payload: CateringRefineRequest) => Promise<CateringPackagesResponse | undefined>;

	private packages: CateringPackage[] = this.initialPackages.map(p => this.clonePackage(p));
	private refinementTexts: string[] = this.initialPackages.map(() => '');
	private refining: boolean[] = this.initialPackages.map(() => false);
	private refinementOpen: boolean[] = this.initialPackages.map(() => false);
	private itemsOpen: boolean[] = this.initialPackages.map(() => true);
	private animateRefined: boolean[] = this.initialPackages.map(() => false);

	private get formattedBudget(): string {
		return this.$n(this.totalBudget, 'currency');
	}

	private toggleItems(index: number): void {
		this.$set(this.itemsOpen, index, !this.itemsOpen[index]);
	}

	private openRefinement(index: number): void {
		this.$set(this.refinementOpen, index, true);
	}

	private closeRefinement(index: number): void {
		this.$set(this.refinementOpen, index, false);
		this.$set(this.refinementTexts, index, '');
	}

	private clonePackage(pkg: CateringPackage): CateringPackage {
		return JSON.parse(JSON.stringify(pkg));
	}

	/**
	 * Send the user's refinement text for the package at `index`, prepend the refined
	 * variant to the top of the list, scroll back to the top, and clear the refinement input.
	 *
	 * @param {number} index
	 * @return {Promise<void>}
	 */
	private async applyRefinement(index: number): Promise<void> {
		// Hold on to the package itself (not just its index). The list can grow
		// while we wait for the server, so we'll use this reference to find the
		// original's new position later.
		const pkg = this.packages[index];

		// Nothing typed — nothing to refine.
		const trimmed = this.refinementTexts[index].trim();
		if (!trimmed) {
			return;
		}

		// Show the loading spinner on this card.
		this.$set(this.refining, index, true);

		try {
			// Build the request body. Optional fields are only attached when we
			// actually have values for them.
			const payload: CateringRefineRequest = {
				prior_package: this.clonePackage(pkg),
				refinement_text: trimmed
			};
			if (this.promptId) {
				payload.prompt_id = this.promptId;
			}
			if (this.dietaryPreferences.length) {
				payload.dietary_preferences = [...this.dietaryPreferences];
			}
			if (this.allergens.length) {
				payload.allergens = [...this.allergens];
			}

			// Send the refinement to the server, then refresh the tokens-remaining
			// counter shown in the modal footer. A failed refresh just leaves the
			// stale counter — no toast needed.
			const response = await this.refinePackage(payload);
			this.fetchUsage().catch(() => {});

			// If the server returned nothing usable, leave the UI untouched.
			const refined = response?.packages?.[0];
			if (!refined) {
				return;
			}

			// Find the original package's current position. Another refinement
			// finishing first could have shifted it. If it's gone, bail out.
			const currentIndex = this.packages.indexOf(pkg);
			if (currentIndex === -1) {
				return;
			}

			// Prepend the refined version to the top of the list. Every
			// per-package state array (texts, loading flags, open/closed flags)
			// mirrors `packages` one-to-one, so each needs a matching new slot
			// at index 0.
			this.packages.unshift({ ...this.clonePackage(refined), is_refined: true });
			this.refinementTexts.unshift('');
			this.refining.unshift(false);
			this.refinementOpen.unshift(false);
			this.itemsOpen.unshift(true);
			// Older entries shift down — clear any lingering animation flags so only
			// the freshly prepended card animates. Start at false, then flip to true
			// across two rAF ticks so the CSS animation actually restarts (one tick
			// alone can get coalesced into the same frame).
			this.animateRefined = this.animateRefined.map(() => false);
			this.animateRefined.unshift(false);
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					this.$set(this.animateRefined, 0, true);
				});
			});

			// The original's refine form has done its job — clear the text
			// and collapse it. The unshift above shifted its index by 1.
			const originalIndex = currentIndex + 1;
			this.$set(this.refinementTexts, originalIndex, '');
			this.$set(this.refinementOpen, originalIndex, false);

			// Smooth-scroll the modal back to the top so the new card is the
			// first thing the user sees.
			this.$nextTick(() => {
				const list = this.$refs.packageList as HTMLElement | undefined;
				list?.firstElementChild?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			});
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.prompts.results.error_refining', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			const finalIndex = this.packages.indexOf(pkg);
			if (finalIndex !== -1) {
				this.$set(this.refining, finalIndex, false);
			}
		}
	}
}
